import React, { useRef, useState, useEffect } from 'react';
import { Lock, Trash2 } from 'lucide-react';
import styles from './form.module.css';

const DrawingForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const canvasRef = useRef(null);
    const isDrawing = useRef(false);
    const lastPos = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [hasSubmitted, setHasSubmitted] = useState(userAnswer != null);

    const strokeColor = '#000000';
    const strokeWidth = 3;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (userAnswer?.drawingData) {
            const img = new Image();
            img.onload = () => ctx.drawImage(img, 0, 0);
            img.src = userAnswer.drawingData;
        }
        setHasSubmitted(userAnswer != null);
        setIsEmpty(userAnswer == null);
    }, [userAnswer]);

    const getPos = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        if (e.touches) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY,
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);

    const startDraw = (e) => {
        if (isDisabled) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        isDrawing.current = true;
        lastPos.current = getPos(e, canvas);
    };

    const draw = (e) => {
        if (!isDrawing.current || isDisabled) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const pos = getPos(e, canvas);
        ctx.beginPath();
        ctx.moveTo(lastPos.current.x, lastPos.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        lastPos.current = pos;
        setIsEmpty(false);
    };

    const endDraw = () => { isDrawing.current = false; };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setIsEmpty(true);
    };

    const handleSubmit = () => {
        if (isPreview || isLocked || isEmpty) return;
        const drawingData = canvasRef.current.toDataURL('image/png');
        onSubmit({ drawingData });
        setHasSubmitted(true);
    };

    const submitDisabled = isPreview || isLocked || isEmpty || (hasSubmitted && !allowUpdateResponse);
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit Drawing') : (!allowUpdateResponse ? 'Submitted ✓' : 'Update drawing');

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Draw your response'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>{content.settings.descText}</div>
            )}
            <div className={`${styles.canvasFrame} ${isDisabled ? styles.disabled : ''}`}>
                <canvas
                    ref={canvasRef}
                    width={600}
                    height={300}
                    style={{ width: '100%', display: 'block', cursor: isDisabled ? 'default' : 'crosshair' }}
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={endDraw}
                    aria-label="Drawing canvas"
                />
            </div>
            {!isDisabled && (
                <div className={styles.canvasToolbar}>
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={clearCanvas}
                        aria-label="Clear drawing"
                    >
                        <Trash2 size={14} aria-hidden="true" /> Clear
                    </button>
                </div>
            )}
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default DrawingForm;
